"use client";

import React, { useEffect, useState } from "react";
import { Calendar, User, Clock, ArrowLeft, Share2, Tag } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";



function RelatedPostsList() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="35878" className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-80 w-full animate-pulse rounded-xl bg-muted/20" data-index={i} />
        ))}
      </div>
    );
  }

  if (error || posts.length === 0) return null;

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
      {posts.map((post, i) => (
        <Card key={post.id} className="overflow-hidden border-border/40 bg-card/40 shadow-sm transition-all hover:shadow-xl hover:border-primary/30" data-index={i}>
          {post.featuredImage && (
            <div className="aspect-video w-full overflow-hidden">
              <img
                src={post.featuredImage}
                alt={post.title}
                className="h-full w-full object-cover transition-transform duration-700 hover:scale-110"
              />
            </div>
          )}
          <CardHeader>
            <CardTitle className="line-clamp-2 text-lg font-default font-bold tracking-tight">
              <Link to={post.link || "#"} className="hover:text-primary transition-colors">
                {post.title}
              </Link>
            </CardTitle>
            <CardDescription className="line-clamp-2 font-default">
              {post.excerpt ? (
                <span dangerouslySetInnerHTML={{ __html: post.excerpt }} />
              ) : (
                "Exploring the future of intelligent systems and scalable digital infrastructure..."
              )}
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Link to={post.link || "#"} className="w-full">
              <Button variant="outline" className="w-full cursor-pointer border-border/60 hover:border-primary/50 hover:bg-primary/5 font-default text-xs uppercase tracking-widest">Read Analysis</Button>
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

function RelatedPostsWrapper({ currentPostId }: { currentPostId: string }) {
  
  const relatedQuery = new WP_Query({
    post_type: "post",
    posts_per_page: 3,
    post__not_in: [parseInt(currentPostId)],
    orderby: "date",
    order: "DESC",
  });

  return (
    <div className="mt-16">
      <div className="mb-8 flex items-center justify-between">
        <h3 className="text-2xl font-default font-bold tracking-tighter md:text-3xl text-foreground">Strategic Insights</h3>
        <Link to="#">
          <Button variant="ghost" className="hidden sm:flex text-muted-foreground hover:text-primary font-default">View All Intelligence</Button>
        </Link>
      </div>
      <PostsProvider wp_query={relatedQuery}>
        <RelatedPostsList />
      </PostsProvider>
    </div>
  );
}



function SinglePostConsumer() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-12">
        <div className="mb-8 h-12 w-3/4 animate-pulse rounded-lg bg-muted/20" />
        <div className="mb-8 h-96 w-full animate-pulse rounded-2xl bg-muted/20" />
        <div className="space-y-4">
          <div className="h-4 w-full animate-pulse rounded bg-muted/20" />
          <div className="h-4 w-full animate-pulse rounded bg-muted/20" />
          <div className="h-4 w-2/3 animate-pulse rounded bg-muted/20" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto flex min-h-[50vh] max-w-4xl flex-col items-center justify-center px-4 text-center">
        <h2 className="text-2xl font-default font-bold text-destructive">System Connection Error</h2>
        <p className="mt-2 text-muted-foreground font-default">The requested intelligence stream is currently unavailable.</p>
        <Button onClick={() => refetch()} className="mt-6 font-default">Reconnect Stream</Button>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="container mx-auto flex min-h-[50vh] max-w-4xl flex-col items-center justify-center px-4 text-center">
        <h2 className="text-2xl font-default font-bold">Intelligence Not Found</h2>
        <p className="mt-2 text-muted-foreground font-default">The specific analysis you are looking for does not exist in our current archives.</p>
        <Link to="/" className="mt-6">
          <Button className="font-default">Return to Command Center</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const authorName = post.author?.name || "Tidal Swing AI Research";
  const authorAvatar = post.author?.avatar?.["96"] || "";
  const publishDate = post.date ? new Date(post.date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }) : "";

  return (
    <article className="container mx-auto max-w-4xl px-4 py-12 md:py-20">
      {}
      <div className="mb-8">
        <Link to="#" className="inline-flex items-center text-sm font-default font-medium text-muted-foreground transition-colors hover:text-primary">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Insights
        </Link>
      </div>

      {}
      <header className="mb-10 text-center md:mb-14 md:text-left">
        <div className="mb-6 flex flex-wrap items-center justify-center gap-3 md:justify-start">
          {post.categories && post.categories.length > 0 && (
            <span className="inline-flex items-center rounded-sm bg-primary/10 px-3 py-1 text-xs font-default font-bold text-primary uppercase tracking-widest">
              {post.categories[0].name}
            </span>
          )}
          <span className="flex items-center text-xs text-muted-foreground font-default">
            <Clock className="mr-1.5 h-3.5 w-3.5" />
            {Math.ceil((post.content?.length || 1000) / 1500)} min analysis
          </span>
        </div>

        <h1 className="mb-6 text-3xl font-default font-bold leading-tight tracking-tighter text-foreground md:text-5xl lg:text-7xl">
          {post.title}
        </h1>

        <div className="flex flex-col items-center gap-6 md:flex-row md:justify-between">
          <div className="flex items-center gap-4">
            <Avatar className="h-12 w-12 border border-border/50 shadow-sm">
              <AvatarImage src={authorAvatar} alt={authorName} />
              <AvatarFallback className="bg-muted"><User className="h-6 w-6 text-muted-foreground" /></AvatarFallback>
            </Avatar>
            <div className="text-left">
              <p className="text-sm font-default font-bold text-foreground">{authorName}</p>
              <div className="flex items-center text-xs text-muted-foreground font-default">
                <Calendar className="mr-1.5 h-3.5 w-3.5" />
                {publishDate}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-9 gap-2 rounded-sm border-border/50 hover:border-primary/50 hover:bg-primary/5 font-default text-xs uppercase tracking-wider">
              <Share2 className="h-4 w-4" />
              Share Analysis
            </Button>
          </div>
        </div>
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-12 overflow-hidden rounded-sm shadow-2xl ring-1 ring-white/10 md:mb-16">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="h-auto w-full object-cover"
          />
        </div>
      )}

      {}
      <div className="prose prose-lg prose-slate dark:prose-invert mx-auto max-w-none md:prose-xl">
        <div
          dangerouslySetInnerHTML={{ __html: post.content || "" }}
          className="font-default [&>p]:mb-6 [&>p]:leading-relaxed [&>p]:text-muted-foreground [&>h2]:mb-4 [&>h2]:mt-12 [&>h2]:text-3xl [&>h2]:font-default [&>h2]:font-bold [&>h2]:tracking-tighter [&>h2]:text-foreground [&>ul]:mb-6 [&>ul]:list-disc [&>ul]:pl-6 [&>li]:mb-3 [&>li]:text-muted-foreground"
        />
      </div>

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="mt-12 flex flex-wrap gap-2 border-t border-border/30 pt-8">
          {post.tags.map((tag) => (
            <Link key={tag.id} to={tag.link || "#"}>
              <span className="inline-flex items-center rounded-sm bg-muted/40 border border-border/40 px-3 py-1 text-xs font-default text-muted-foreground transition-all hover:bg-primary/10 hover:text-primary hover:border-primary/30">
                <Tag className="mr-1.5 h-3.5 w-3.5" />
                {tag.name}
              </span>
            </Link>
          ))}
        </div>
      )}

      <Separator className="my-12 md:my-16 opacity-30" />

      {}
      <div className="rounded-sm bg-card/40 border border-border/40 backdrop-blur-md p-8 md:p-10">
        <div className="flex flex-col items-center gap-6 text-center md:flex-row md:text-left">
          <Avatar className="h-20 w-20 border-2 border-primary/20 shadow-xl md:h-24 md:w-24">
            <AvatarImage src={authorAvatar} alt={authorName} />
            <AvatarFallback className="bg-muted"><User className="h-10 w-10 text-muted-foreground" /></AvatarFallback>
          </Avatar>
          <div className="flex-1 space-y-2">
            <h3 className="text-xl font-default font-bold text-foreground">About {authorName}</h3>
            <p className="text-muted-foreground font-default leading-relaxed">
              {post.author?.description || `Infrastructure architect and strategy lead at Tidal Swing AI. Focused on building sophisticated digital ecosystems, sovereign AI computing, and enterprise-grade systems integration.`}
            </p>
            {post.author?.link && (
              <Link to={post.author.link} className="inline-block text-sm font-default font-bold text-primary hover:underline underline-offset-4">
                View all analysis by {authorName}
              </Link>
            )}
          </div>
        </div>
      </div>

      {}
      <RelatedPostsWrapper currentPostId={post.id} />
    </article>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = (window as any).wvcClient?.getCurrentWPQueryParams() || { post_type: "post" };
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-post-template-section" className="dark bg-background text-foreground selection:bg-primary/30 selection:text-primary-foreground">
      <PostsProvider wp_query={wp_query}>
        <SinglePostConsumer />
      </PostsProvider>
    </section>
  );
}